import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { formatQAR } from "../lib/money";
const ACTIONS = [
    { path: "write-off", label: "Request Write-off" },
    { path: "withdraw", label: "Request Withdrawal" },
    { path: "close-with-outstanding", label: "Close With Outstanding" },
    { path: "cancel", label: "Cancel Case" },
    { path: "change-receivable", label: "Change Receivable", needsAmount: true },
];
const describe = (r) => {
    const p = r.payload || {};
    const parts = [];
    if (p.newAmountMinor != null) parts.push(`New receivable ${formatQAR(p.newAmountMinor / 100)}`);
    if (p.chequeNumber) parts.push(`Cheque ${p.chequeNumber}`);
    if (p.reason) parts.push(p.reason);
    return parts.join(" · ");
};
export function ApprovalsPanel({ caseId, onCaseUpdated }) {
    const [items, setItems] = useState([]);
    const [error, setError] = useState(null);
    const [openForm, setOpenForm] = useState(null);
    const [reason, setReason] = useState("");
    const [newAmount, setNewAmount] = useState("");
    const [busy, setBusy] = useState(false);
    function load() {
        api
            .get(`/cases/${caseId}/approvals`)
            .then((r) => setItems(r.items))
            .catch(() => setError("Couldn't load approval requests."));
    }
    useEffect(load, [caseId]);
    const form = ACTIONS.find((a) => a.path === openForm);
    async function submitAction() {
        if (!reason.trim()) {
            setError("A reason is required.");
            return;
        }
        if (form?.needsAmount && (newAmount === "" || Number(newAmount) < 0)) {
            setError("Enter the new receivable amount.");
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await api.post(`/cases/${caseId}/actions/${openForm}`, {
                reason: reason.trim(),
                ...(form?.needsAmount ? { newAmount: Number(newAmount) } : {}),
            });
            setReason("");
            setNewAmount("");
            setOpenForm(null);
            load();
        }
        catch (e) {
            setError(e?.message && e.status !== 0 ? e.message : "Couldn't submit that request.");
        }
        finally {
            setBusy(false);
        }
    }
    async function decide(id, decision) {
        let comment;
        if (decision === "REJECTED") {
            comment = window.prompt("Reason for rejecting (sent to the requester):");
            if (!comment || !comment.trim()) return;
        }
        setBusy(true);
        setError(null);
        try {
            await api.post(`/approvals/${id}/decide`, { decision, comment: comment?.trim() });
            load();
            if (onCaseUpdated) onCaseUpdated();
        }
        catch (e) {
            setError(e?.message && e.status !== 0 ? e.message : "Couldn't record that decision.");
        }
        finally {
            setBusy(false);
        }
    }
    return (<div>
      {error && <div style={{ color: "var(--st-risk)", fontSize: "0.85rem", marginBottom: 12 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 18 }}>
        {ACTIONS.map((a) => (<button key={a.path} onClick={() => setOpenForm(openForm === a.path ? null : a.path)} style={{ ...actionBtn, ...(openForm === a.path ? { borderColor: "var(--brand)", color: "var(--brand)" } : {}) }}>
            {a.label}
          </button>))}
      </div>

      {openForm && (<div style={{ border: "1px solid var(--line-soft)", borderRadius: 8, padding: 14, marginBottom: 18 }}>
          <div style={{ fontSize: "0.78rem", color: "var(--ink-soft)", marginBottom: 8 }}>
            {form?.label} — nothing changes on the case until the request is approved.
          </div>
          {form?.needsAmount && (<input type="number" min="0" placeholder="New receivable amount (QAR)" value={newAmount} onChange={(e) => setNewAmount(e.target.value)} style={{ width: "100%", border: "1px solid var(--line)", borderRadius: 8, padding: 10, fontSize: "0.85rem", marginBottom: 10 }}/>)}
          <textarea placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} style={{ width: "100%", border: "1px solid var(--line)", borderRadius: 8, padding: 10, fontSize: "0.85rem", fontFamily: "inherit", marginBottom: 10 }}/>
          <button onClick={submitAction} disabled={busy} style={actionBtn}>
            Submit request
          </button>
        </div>)}

      {items.map((r) => (<div key={r.id} style={{ border: `1px solid ${r.isMyStep ? "var(--accent)" : "var(--line-soft)"}`, borderRadius: 10, padding: "14px 16px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div className="mono" style={{ fontSize: "0.65rem", color: "var(--accent-ink)", textTransform: "uppercase" }}>
              {r.actionType.replace(/_/g, " ")} · {r.status.replace(/_/g, " ")}
            </div>
            <div style={{ fontSize: "0.86rem", fontWeight: 600, marginTop: 2 }}>
              Step {Math.min(r.currentStep, r.steps.length)} of {r.steps.length} · requested by {r.requestedByName} on {new Date(r.requestedAt).toLocaleDateString()}
            </div>
            {describe(r) && <div style={{ fontSize: "0.8rem", color: "var(--ink-soft)", marginTop: 3 }}>{describe(r)}</div>}
            {r.steps.filter((s) => s.decision).map((s) => (<div key={s.id} className="mono" style={{ fontSize: "0.7rem", color: s.decision === "REJECTED" ? "var(--st-risk)" : "var(--st-open)", marginTop: 3 }}>
                Step {s.sequence}: {s.decision.toLowerCase()} by {s.decidedByName}{s.comment ? ` — ${s.comment}` : ""}
              </div>))}
          </div>
          {r.isMyStep ? (<div style={{ display: "flex", gap: 8, flex: "0 0 auto" }}>
              <button onClick={() => decide(r.id, "REJECTED")} disabled={busy} style={rejectBtn}>
                Reject
              </button>
              <button onClick={() => decide(r.id, "APPROVED")} disabled={busy} style={approveBtn}>
                Approve
              </button>
            </div>) : (<span className="mono" style={{ fontSize: "0.72rem", color: "var(--ink-faint)", flex: "0 0 auto" }}>
              {r.status === "APPROVED" || r.status === "REJECTED" ? "Decided" : "Awaiting approver"}
            </span>)}
        </div>))}
      {items.length === 0 && !error && <div style={{ color: "var(--ink-faint)", fontSize: "0.85rem" }}>No approval requests on this case.</div>}
    </div>);
}
const actionBtn = {
    border: "1px solid var(--line)",
    background: "var(--surface)",
    color: "var(--ink-soft)",
    fontWeight: 600,
    fontSize: "0.8rem",
    padding: "8px 14px",
    borderRadius: 8,
    cursor: "pointer",
};
const approveBtn = {
    background: "var(--st-open)",
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.82rem",
    padding: "8px 16px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
};
const rejectBtn = {
    background: "transparent",
    border: "1px solid var(--st-risk)",
    color: "var(--st-risk)",
    fontWeight: 600,
    fontSize: "0.82rem",
    padding: "8px 16px",
    borderRadius: 8,
    cursor: "pointer",
};
